import { useEffect, useState } from 'react';
import { Search, Sparkles, Trash2 } from 'lucide-react';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/Dialog';
import { Button } from '@/components/ui/Button';
import { Input, Textarea } from '@/components/ui/Input';
import { FormField } from '@/components/ui/FormField';
import { SegmentedControl } from '@/components/ui/SegmentedControl';
import { ErrorBanner, useDebounced } from '../borrow/ui';
import { searchMedicines } from '../api';
import type { MedicineSearchResult } from '../types';
import { fetchIndentSuggestion } from './api';
import { useCreateIndent } from './useIndents';
import type { IndentDetail, IndentPriority, TransferStore } from './types';

interface Line {
  medicine_id: number;
  name: string;
  quantity: string;
  /** "On hand 30 · min 100" when the line came from the reorder levels. */
  hint?: string;
}

const selectClass = 'h-9 w-full rounded-[var(--radius)] border border-input bg-card px-2 text-[13px]';
const medName = (m: MedicineSearchResult) => [m.generic_name, m.strength, m.brand_name ? `(${m.brand_name})` : null].filter(Boolean).join(' ');

/**
 * Raise an indent: one store asks another for medicines (not batches — the
 * supplying store picks batches when it sends them). "Suggest from reorder
 * levels" fills in everything the requesting store is at or below its
 * reorder point for, net of stock already on its way.
 */
export default function NewIndentDialog({ stores, defaultRequestingId, defaultSupplyingId, prefillSuggestion, onClose, onCreated }: {
  stores: TransferStore[];
  defaultRequestingId?: number;
  defaultSupplyingId?: number;
  /** Open with the reorder-level suggestion already loaded. */
  prefillSuggestion?: boolean;
  onClose: () => void;
  onCreated: (indent: IndentDetail) => void;
}) {
  const active = stores.filter((s) => s.active);
  const [requestingId, setRequestingId] = useState<number | null>(defaultRequestingId ?? null);
  const [supplyingId, setSupplyingId] = useState<number | null>(defaultSupplyingId ?? active.find((s) => s.id !== defaultRequestingId)?.id ?? null);
  const [priority, setPriority] = useState<IndentPriority>('routine');
  const [lines, setLines] = useState<Line[]>([]);
  const [notes, setNotes] = useState('');
  const [search, setSearch] = useState('');
  const [results, setResults] = useState<MedicineSearchResult[]>([]);
  const [formError, setFormError] = useState('');
  const [suggesting, setSuggesting] = useState(false);
  const debounced = useDebounced(search.trim());
  const create = useCreateIndent();

  useEffect(() => {
    if (debounced.length < 2) { setResults([]); return; }
    let live = true;
    searchMedicines(debounced).then((r) => { if (live) setResults(r); }).catch(() => { if (live) setResults([]); });
    return () => { live = false; };
  }, [debounced]);

  const suggest = async (storeId: number | null) => {
    if (!storeId) return setFormError('Choose the store asking for stock first.');
    setSuggesting(true);
    setFormError('');
    try {
      const rows = await fetchIndentSuggestion(storeId);
      if (!rows.length) {
        setFormError('Nothing at this store is at or below its reorder level (or what is low is already on its way).');
      } else {
        setLines((ls) => {
          const have = new Set(ls.map((l) => l.medicine_id));
          return [...ls, ...rows.filter((r) => !have.has(r.medicine_id)).map((r) => ({
            medicine_id: r.medicine_id, name: r.item_name, quantity: String(r.quantity),
            hint: `On hand ${r.on_hand} · min ${r.min_level}${r.max_level != null ? ` · max ${r.max_level}` : ''}${r.in_transit_in + r.open_indent_qty ? ` · ${r.in_transit_in + r.open_indent_qty} already coming` : ''}`,
          }))];
        });
      }
    } catch (e) {
      setFormError(e instanceof Error ? e.message : 'Could not load the suggestion.');
    }
    setSuggesting(false);
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps -- once, on open
  useEffect(() => { if (prefillSuggestion && defaultRequestingId) void suggest(defaultRequestingId); }, []);

  const add = (m: MedicineSearchResult) => {
    if (!lines.some((l) => l.medicine_id === m.id)) setLines((ls) => [...ls, { medicine_id: m.id, name: medName(m), quantity: '' }]);
    setSearch('');
    setResults([]);
  };

  const submit = () => {
    setFormError('');
    if (!requestingId || !supplyingId) return setFormError('Choose which store is asking and which store supplies it.');
    if (requestingId === supplyingId) return setFormError('A store can’t indent from itself.');
    if (!lines.length) return setFormError('Add at least one medicine.');
    for (const l of lines) {
      const q = Number(l.quantity);
      if (!Number.isInteger(q) || q <= 0) return setFormError(`Enter a quantity for ${l.name}.`);
    }
    create.mutate({
      requesting_store_id: requestingId,
      supplying_store_id: supplyingId,
      priority,
      notes: notes.trim() || undefined,
      items: lines.map((l) => ({ medicine_id: l.medicine_id, quantity: Number(l.quantity) })),
    }, { onSuccess: onCreated });
  };

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-[720px]">
        <DialogHeader><DialogTitle>New indent</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="grid gap-3 sm:grid-cols-2">
            <FormField label="Store asking for stock">
              <select className={selectClass} value={requestingId ?? ''} onChange={(e) => setRequestingId(e.target.value ? Number(e.target.value) : null)}>
                <option value="">Choose…</option>
                {active.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            </FormField>
            <FormField label="Supplied by">
              <select className={selectClass} value={supplyingId ?? ''} onChange={(e) => setSupplyingId(e.target.value ? Number(e.target.value) : null)}>
                <option value="">Choose…</option>
                {active.filter((s) => s.id !== requestingId).map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            </FormField>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <SegmentedControl
              options={[{ value: 'routine' as const, label: 'Routine' }, { value: 'urgent' as const, label: 'Urgent' }]}
              value={priority} onChange={setPriority} size="sm" className="w-[200px]"
            />
            <span className="flex-1" />
            <Button variant="ghost" size="sm" onClick={() => suggest(requestingId)} loading={suggesting}>
              <Sparkles className="h-4 w-4" /> Suggest from reorder levels
            </Button>
          </div>

          <FormField label="Add a medicine">
            <div className="relative">
              <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
              <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Generic or brand name" className="pl-8" />
            </div>
          </FormField>
          {results.length > 0 && (
            <div className="max-h-[160px] overflow-y-auto rounded-[var(--radius)] border border-border">
              {results.map((m) => (
                <button key={m.id} type="button" onClick={() => add(m)}
                  className="block w-full border-b border-border px-3 py-2 text-left text-[13px] last:border-b-0 hover:bg-muted">
                  {medName(m)} {m.dosage_form && <span className="text-muted-foreground">· {m.dosage_form}</span>}
                </button>
              ))}
            </div>
          )}

          {lines.length > 0 && (
            <div className="divide-y divide-border rounded-[var(--radius)] border border-border">
              {lines.map((l) => (
                <div key={l.medicine_id} className="flex flex-wrap items-center gap-2 px-3 py-2">
                  <div className="min-w-0 flex-1 text-[13px]">
                    <span className="font-semibold">{l.name}</span>
                    {l.hint && <span className="block text-[11.5px] text-muted-foreground">{l.hint}</span>}
                  </div>
                  <Input type="number" min={1} inputMode="numeric" className="w-24" placeholder="Qty"
                    aria-label={`Quantity of ${l.name}`}
                    value={l.quantity}
                    onChange={(e) => setLines((ls) => ls.map((x) => (x.medicine_id === l.medicine_id ? { ...x, quantity: e.target.value } : x)))} />
                  <Button variant="ghost" size="sm" aria-label={`Remove ${l.name}`} onClick={() => setLines((ls) => ls.filter((x) => x !== l))}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              ))}
            </div>
          )}

          <FormField label="Notes (optional)">
            <Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="e.g. Ward 3 weekend cover" />
          </FormField>
          <ErrorBanner message={formError || (create.error instanceof Error ? create.error.message : null)} />
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} loading={create.isPending}>Submit indent</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
